/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { EpisodeInfo, SeasonsData } from 'client-code/episode-guide';

import { APPLICATION_FEATURE_AREA } from '../../config';
import { EpisodeGuideError } from '../error/EpisodeGuideError';
import { VodBrowseError } from '../error/VodBrowseError';
import { NdvrInteractor, ServicesClientImpl, VodInteractor } from '../interactors';
import { Locale, LocaleKeys } from '../locale';
import { Log } from '../logging';
import { SharedPrefConstants, SharedPreferences } from '../storage';
import { TUpcomingOffersList, TVodBrowseData } from '../types';

export class ListView {
    private readonly TAG: string = 'ListView';

    private mService: ServicesClientImpl;

    private mVodServices: VodInteractor;

    private ndvrInteractor: NdvrInteractor;

    public constructor() {
        this.mService = new ServicesClientImpl();
        this.mVodServices = new VodInteractor();
        this.ndvrInteractor = new NdvrInteractor();
    }

    /**
     * Fetches the VodItemList for My Rentals.
     * @param id - The parent ID for the VodItemList.
     * @returns A promise that resolves to a VodBrowseData
     *  object containing the VodItemList and total count.
     */
    public async getMyRentalsVodBrowseData(
        id: string,
        offset: number = 0, // Default offset for pagination
        limit: number = 18, // Default limit for number of items to fetch (same as streamer)
    ): Promise<TVodBrowseData> {
        try {
            const response = await this.mVodServices.getVodBrowseData({
                parentVodItemId: id,
                offset,
                limit,
                includeAdult: this.mService.isPCHideAdultContentEnabled(),
            });
            const { vodItemList, totalCount } = response;
            const listViewScreenSharedPreferenceEditor = new SharedPreferences(
                SharedPrefConstants.LIST_SCREEN_PREF_KEY,
            ).edit();
            listViewScreenSharedPreferenceEditor.putNumber(
                SharedPrefConstants.LIST_SCREEN_TOTAL_COUNT,
                totalCount,
            );
            listViewScreenSharedPreferenceEditor.apply();
            return { vodItemList, totalCount };
        } catch (error: any) {
            const vodBrowseServiceError = error?.response?.data;
            const vodBrowseError = new VodBrowseError(
                vodBrowseServiceError?.code,
                vodBrowseServiceError?.message,
                Locale.getString(LocaleKeys.LIST_VIEW_SCREEN_ERROR_MESSAGE),
            );
            const description = vodBrowseError
                .getDescription()
                .replace('ERROR_CODE', vodBrowseError.getCode());

            vodBrowseError.setDescription(description);
            Diagnostics.error(this.TAG, 'VodBrowseError', vodBrowseError);
            throw new Error(`${description}`);
        }
    }

    public async getUpcomingData(
        id: string,
        offset: number = 0,
        limit: number = 18,
    ): Promise<TUpcomingOffersList> {
        try {
            const res = await this.ndvrInteractor.getUpcomingOffersForCollectionId({
                objectId: id,
                minEndTime: new Date().toISOString(),
                offset,
                limit,
                screenType: APPLICATION_FEATURE_AREA.LIST_VIEW_SCREEN,
            });
            const listViewScreenSharedPreferenceEditor = new SharedPreferences(
                SharedPrefConstants.LIST_SCREEN_PREF_KEY,
            ).edit();
            listViewScreenSharedPreferenceEditor.putNumber(
                SharedPrefConstants.LIST_SCREEN_TOTAL_COUNT,
                res.totalCount,
            );
            listViewScreenSharedPreferenceEditor.apply();
            return res;
        } catch (error: any) {
            Log.e(this.TAG, error);
            Diagnostics.error(this.TAG, 'UpcomingOffersError', error);
            throw new Error(error.message);
        }
    }

    public async getSeasonData(
        id: string,
        partnerId?: string,
        collapsedVodItemId?: string,
        offset: number = 0,
        limit: number = 19,
    ): Promise<EpisodeInfo[]> {
        try {
            const seasonResponse: SeasonsData = await this.ndvrInteractor.getSeasonData({
                unifiedCollectionId: id,
                includeStickyInfo: true,
                filter: 'vodOnDemand',
                partnerId: partnerId || '',
                collapsedVodItemId: collapsedVodItemId || '',
            });
            const episodesResponse = await this.ndvrInteractor.getEpisodes({
                unifiedCollectionId: id,
                partnerId: partnerId || '',
                collapsedVodItemId: collapsedVodItemId || '',
                filter: 'vodOnDemand',
                limit: seasonResponse.totalEpisodesCount,
                offset: 0,
            });

            const { episodesToShow } = ListView.calculateEpisodeSlicing(
                seasonResponse,
                episodesResponse.episodeInfo || [],
                offset,
                limit,
            );
            const listViewScreenSharedPreferenceEditor = new SharedPreferences(
                SharedPrefConstants.LIST_SCREEN_PREF_KEY,
            ).edit();
            listViewScreenSharedPreferenceEditor.putNumber(
                SharedPrefConstants.LIST_SCREEN_TOTAL_COUNT,
                seasonResponse.totalEpisodesCount,
            );
            listViewScreenSharedPreferenceEditor.apply();
            return episodesToShow;
        } catch (error: any) {
            const episodeGuideServiceError = error?.response?.data;
            const episodeGuideError = new EpisodeGuideError(
                episodeGuideServiceError?.code,
                episodeGuideServiceError?.message,
                Locale.getString(LocaleKeys.LIST_VIEW_SCREEN_ERROR_MESSAGE),
            );
            const description = episodeGuideError
                .getDescription()
                .replace('ERROR_CODE', episodeGuideError.getCode());

            episodeGuideError.setDescription(description);
            Diagnostics.error(this.TAG, 'EpisodeGuideError', episodeGuideError);
            throw new Error(`${description}`);
        }
    }

    private static calculateEpisodeSlicing(
        seasonsData: SeasonsData,
        allEpisodes: EpisodeInfo[],
        offset: number,
        limit: number,
    ) {
        // Group episodes by season to understand the structure
        const episodesBySeason = allEpisodes.reduce(
            (acc: { [key: number]: EpisodeInfo[] }, episode: EpisodeInfo) => {
                const season = episode.seasonOrYear || 0;
                if (!acc[season]) acc[season] = [];
                acc[season].push(episode);
                return acc;
            },
            {},
        );
        // Calculate how many episodes we need based on offset and available space for headers
        let totalItemsToSkip = offset;
        let episodesIndex = 0;

        // eslint-disable-next-line no-restricted-syntax
        for (const season of seasonsData.seasons || []) {
            const seasonKey = season.seasonOrYear || 0;
            const seasonHeaderSize = 2; // Assuming each season header takes 2 slots
            const seasonEpisodes = episodesBySeason[seasonKey] || [];
            const seasonTotalItems = seasonHeaderSize + seasonEpisodes?.length;

            if (totalItemsToSkip >= seasonTotalItems) {
                totalItemsToSkip -= seasonTotalItems;
                episodesIndex += seasonEpisodes?.length || 0;
            } else if (totalItemsToSkip >= seasonHeaderSize) {
                const episodesToSkip = totalItemsToSkip - seasonHeaderSize;
                episodesIndex += episodesToSkip;
                totalItemsToSkip = 0;
                break;
            } else {
                // We are within the header, so we don't skip any episodes
                totalItemsToSkip = 0;
                break;
            }
        }
        const remainingEpisodes = allEpisodes.slice(episodesIndex);
        const uniqueSeasonsRemaining = [
            ...new Set(
                remainingEpisodes
                    .map((ep: EpisodeInfo) => ep.seasonOrYear)
                    .filter((season): season is number => season !== undefined),
            ),
        ];
        const headersSpaceNeeded = uniqueSeasonsRemaining.length * 2;

        // Calculate how many episodes we can show
        const maxEpisodesCanShow = Math.max(0, limit - headersSpaceNeeded);
        const episodesToShow = remainingEpisodes.slice(0, maxEpisodesCanShow);

        const seasonsToShow = new Set(uniqueSeasonsRemaining);
        return {
            episodesToShow,
            seasonsToShow,
            actualEpisodesNeeded: episodesToShow.length,
        };
    }
}
